import { useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useScreeningConfig,
  useCreateScreeningConfig,
  useUpdateScreeningConfig,
} from '../../../hooks/queries/useVisitorManagement';
import type { ScreeningConfigurationWrite } from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';

export function useScreeningConfigCrud() {
  const { data, isLoading, error } = useScreeningConfig();
  const createMutation = useCreateScreeningConfig();
  const updateMutation = useUpdateScreeningConfig();

  const config = data?.[0] ?? null;

  const handleSubmit = (
    values: ScreeningConfigurationWrite,
    setError: UseFormSetError<ScreeningConfigurationWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof ScreeningConfigurationWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({ title: 'Save failed', description: err.message, type: 'error' });
    };

    if (config) {
      updateMutation.mutate(
        { publicId: config.public_id, data: values },
        {
          onSuccess: () =>
            toaster.create({ title: 'Screening configuration saved', type: 'success' }),
          onError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () =>
          toaster.create({ title: 'Screening configuration created', type: 'success' }),
        onError,
      });
    }
  };

  const initialValues: ScreeningConfigurationWrite = useMemo(
    () =>
      config
        ? {
            is_enabled: config.is_enabled,
            auto_screen_on_checkin: config.auto_screen_on_checkin,
            cache_ttl_hours: config.cache_ttl_hours,
            nsoprc_enabled: config.nsoprc_enabled,
            state_registry_enabled: config.state_registry_enabled,
            state_code: config.state_code,
          }
        : {
            is_enabled: false,
            auto_screen_on_checkin: true,
            cache_ttl_hours: 24,
            nsoprc_enabled: true,
            state_registry_enabled: false,
            state_code: '',
          },
    [config],
  );

  return {
    isLoading,
    error,
    config,
    handleSubmit,
    initialValues,
  };
}
